/** Utilities (v1 utilities.js utilitiesView). One place to see + edit every
 * deal's electric/water accounts (v1 UTIL_FIELDS on the deal's edits), plus the
 * monthly "Pay Utilities" reminder task (v1 _utilMonthlyTask). The four fields
 * are ordinary deal fields, so edits ride the existing deals door — no new
 * migration. The task rides the tasks door. */
import { useEffect, useMemo, useState } from "react";
import type { Deal } from "../deals/api";

const UTIL_FIELDS: { key: string; label: string }[] = [
  { key: "t_elecweb", label: "Electric site" },
  { key: "t_elecacct", label: "Electric acct #" },
  { key: "t_waterweb", label: "Water site" },
  { key: "t_wateracct", label: "Water acct #" },
];
const isUrl = (v: string): boolean => /^https?:\/\//i.test(v.trim());
const fieldStr = (d: Deal, key: string): string => {
  const v = d.fields[key];
  return typeof v === "string" ? v : v == null ? "" : String(v);
};

export function UtilitiesPage({
  deals,
  tasks,
  onEditField,
  onAddTask,
  onOpenDeal,
  onLogExpense,
}: {
  deals: Deal[];
  tasks: { title: string }[];
  onEditField: (natKey: string, field: string, value: string | null) => void;
  onAddTask: (title: string, opts?: { deal?: string }) => void;
  /** v1 utilOpenDeal — jump to this deal (its drawer holds expenses + accounts) */
  onOpenDeal: (natKey: string) => void;
  /** v1 utilLogExpense — jump to Property Expenses with a Utilities expense
   * pre-filled for this property. */
  onLogExpense: (address: string) => void;
}) {
  const now = new Date();
  const monthYear = now.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const taskTitle = "Pay Utilities — " + monthYear;
  const hasTask = useMemo(() => tasks.some((t) => (t.title || "").trim() === taskTitle), [tasks, taskTitle]);

  return (
    <div className="leads exppage">
      <div className="exphead">
        <h1>Utilities</h1>
        <div className="expacts">
          <button className="btn slim primary" disabled={hasTask} onClick={() => onAddTask(taskTitle, { deal: "Utilities" })}>
            {hasTask ? `✓ ${monthYear} reminder added` : `+ Pay-Utilities task · ${monthYear}`}
          </button>
        </div>
      </div>

      <div className="expqbo">
        Electric &amp; water accounts for every deal, in one place. On the 1st of each month, add the recurring
        “Pay Utilities” task so nothing lapses. Paste a login URL and an <b>open ↗</b> link appears.
      </div>

      <div className="utilremind">
        <span className="utilbell" aria-hidden="true">🔔</span>
        <span>Auto-reminder: a <b>Pay Utilities</b> task is created on the 1st of every month.</span>
      </div>

      {deals.length ? (
        <div className="utilcards">
          {deals.map((d) => (
            <div className="utilcard" key={d.natKey}>
              <div className="utilcardhead">
                {/* v1 utilOpenDeal — the property name opens the deal (its drawer
                    manages accounts); v1 utilLogExpense — the button jumps to
                    Property Expenses with a Utilities expense pre-filled. */}
                <button className="utilcardtitle" title="Open deal — manage accounts & expenses" onClick={() => onOpenDeal(d.natKey)}>⚡ {d.address.split(",")[0]} ↗</button>
                <button className="btn slim primary utillogexp" title="Log a Utilities expense for this property" onClick={() => onLogExpense(d.address)}>＄ Log Expense</button>
              </div>
              <div className="utilgrid">
                {UTIL_FIELDS.map((f) => (
                  <EditableCell
                    key={f.key}
                    label={f.label}
                    value={fieldStr(d, f.key)}
                    onCommit={(v) => onEditField(d.natKey, f.key, v.trim() === "" ? null : v.trim())}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="list"><div className="expempty">No deals yet — utility accounts show up here once you have transactions.</div></div>
      )}
    </div>
  );
}

function EditableCell({ label, value, onCommit }: { label: string; value: string; onCommit: (v: string) => void }) {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  return (
    <div className="utilfield">
      <div className="utilfieldlabel">
        <span>{label}</span>
        {isUrl(v) ? <a href={v} target="_blank" rel="noreferrer" className="utillink" title="Open">open ↗</a> : null}
      </div>
      <input
        className="utilfldinput"
        value={v}
        placeholder="—"
        onChange={(e) => setV(e.target.value)}
        onBlur={() => { if (v !== value) onCommit(v); }}
      />
    </div>
  );
}
